import { Badge, Eyebrow, StrongText, Subheading, SurfaceCard, SurfacePanel, SurfaceRow } from '../../../components/ui'
import type { ComponentProps } from 'react'
import { SLEEP_SUMMARY, SLEEP_HISTORY } from '../constants'
import { uiClasses } from '../../shared/uiClasses'
import { GradientSphereMetricCard, sphereColorAt } from '../components/GradientSphereCard'

const TEXT_PRIMARY = uiClasses.textPrimary
const TEXT_SECONDARY = uiClasses.textSecondary
const TEXT_SUBTLE = uiClasses.textSubtle
const BAR_TRACK = uiClasses.barTrack
type BadgeColor = ComponentProps<typeof Badge>['variant']
type SleepStageKey = 'deep' | 'rem' | 'light' | 'awake'

const STAGE_COLORS: Record<SleepStageKey, string> = {
  deep: 'dash-viz-tone-1',
  rem: 'dash-viz-tone-2',
  light: 'dash-viz-tone-3',
  awake: 'dash-viz-tone-4',
}

const STAGE_LABELS: Array<{ color: string; key: SleepStageKey; label: string }> = [
  { key: 'deep', label: 'Deep', color: 'dash-viz-tone-1' },
  { key: 'rem', label: 'REM', color: 'dash-viz-tone-2' },
  { key: 'light', label: 'Light', color: 'dash-viz-tone-3' },
  { key: 'awake', label: 'Awake', color: 'dash-viz-tone-4' },
]

function scoreBadgeColor(score: number): BadgeColor {
  if (score >= 80) return 'green'
  if (score >= 65) return 'amber'
  return 'red'
}

function scoreBarColor(score: number): string {
  if (score >= 80) return 'dash-viz-score-high'
  if (score >= 65) return 'dash-viz-score-mid'
  return 'dash-viz-score-low'
}

function formatHours(hoursValue: number): string {
  const hours = Math.floor(hoursValue)
  const minutes = Math.round((hoursValue - hours) * 60)
  return `${hours}h ${minutes}m`
}

export default function SleepTab() {
  const count = SLEEP_HISTORY.length
  const avgDeep = SLEEP_HISTORY.reduce((sum, night) => sum + night.deep, 0) / count
  const avgRem = SLEEP_HISTORY.reduce((sum, night) => sum + night.rem, 0) / count
  const avgLight = SLEEP_HISTORY.reduce((sum, night) => sum + night.light, 0) / count
  const avgAwake = SLEEP_HISTORY.reduce((sum, night) => sum + night.awake, 0) / count
  const avgTotal = avgDeep + avgRem + avgLight + avgAwake

  const stageAverages = [
    { key: 'deep', label: 'Deep', hours: avgDeep, pct: ((avgDeep / avgTotal) * 100).toFixed(0), color: 'dash-viz-tone-1' },
    { key: 'rem', label: 'REM', hours: avgRem, pct: ((avgRem / avgTotal) * 100).toFixed(0), color: 'dash-viz-tone-2' },
    { key: 'light', label: 'Light', hours: avgLight, pct: ((avgLight / avgTotal) * 100).toFixed(0), color: 'dash-viz-tone-3' },
    { key: 'awake', label: 'Awake', hours: avgAwake, pct: ((avgAwake / avgTotal) * 100).toFixed(0), color: 'dash-viz-tone-4' },
  ]

  const maxScore = Math.max(...SLEEP_HISTORY.map((night) => night.score))
  const nightsAboveTarget = SLEEP_HISTORY.filter((night) => night.score >= 80).length
  const bestNight = SLEEP_HISTORY.reduce((best, night) =>
    night.score > best.score ? night : best
  )
  const lowRecoveryNights = SLEEP_HISTORY.filter((night) => night.score < 70).length

  const overviewStats = [
    { label: 'Avg Duration', value: SLEEP_SUMMARY.avgDuration },
    { label: 'Avg Score', value: `${SLEEP_SUMMARY.avgScore}/100` },
    { label: 'Avg REM', value: SLEEP_SUMMARY.avgRem },
    { label: 'Consistency', value: `${SLEEP_SUMMARY.consistency}%` },
  ]

  return (
    <div className="space-y-6">
      <SurfaceCard as="article" className="sleep-overview-shell">
        <div className="sleep-overview-layout">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Subheading variant="dashboard">Sleep intelligence board</Subheading>
              <Badge variant="zinc">
                {nightsAboveTarget}/7 high-quality nights
              </Badge>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Badge variant="zinc">
                Avg latency {SLEEP_SUMMARY.avgLatency}
              </Badge>
              <Badge variant="zinc">
                Avg wake-ups {SLEEP_SUMMARY.avgWakeUps}
              </Badge>
            </div>
          </div>

          <div className="sleep-inline-metrics">
            {overviewStats.map((stat, index) => (
              <GradientSphereMetricCard
                key={stat.label}
                label={stat.label}
                value={stat.value}
                sphereColor={sphereColorAt(index)}
              />
            ))}
          </div>
        </div>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Subheading variant="dashboard">Last 7 nights</Subheading>
          <div className="flex flex-wrap gap-2">
            {STAGE_LABELS.map((stage) => (
              <span key={stage.key} className={`flex items-center gap-1.5 text-xs/5 ${TEXT_SUBTLE}`}>
                <span className={`inline-block size-2.5 rounded-full ${stage.color}`} />
                {stage.label}
              </span>
            ))}
          </div>
        </div>

        <div className="sleep-night-layout mt-4">
          <div className="sleep-night-stream space-y-2">
            {SLEEP_HISTORY.map((night) => {
              const total = night.deep + night.rem + night.light + night.awake
              return (
                <SurfaceRow key={night.id} className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <span className={`w-16 shrink-0 text-sm font-medium ${TEXT_PRIMARY}`}>{night.date}</span>
                  <span className={`w-16 shrink-0 text-sm ${TEXT_SECONDARY}`}>{formatHours(night.duration)}</span>
                  <span className="w-16 shrink-0">
                    <Badge variant={scoreBadgeColor(night.score)}>{night.score}</Badge>
                  </span>

                  <div className="flex h-4 flex-1 overflow-hidden rounded-full">
                    <div
                      className={`${STAGE_COLORS.deep} transition-all`}
                      style={{ width: `${(night.deep / total) * 100}%` }}
                      title={`Deep: ${formatHours(night.deep)}`}
                    />
                    <div
                      className={`${STAGE_COLORS.rem} transition-all`}
                      style={{ width: `${(night.rem / total) * 100}%` }}
                      title={`REM: ${formatHours(night.rem)}`}
                    />
                    <div
                      className={`${STAGE_COLORS.light} transition-all`}
                      style={{ width: `${(night.light / total) * 100}%` }}
                      title={`Light: ${formatHours(night.light)}`}
                    />
                    <div
                      className={`${STAGE_COLORS.awake} transition-all`}
                      style={{ width: `${(night.awake / total) * 100}%` }}
                      title={`Awake: ${formatHours(night.awake)}`}
                    />
                  </div>

                  <span className={`w-40 shrink-0 text-right text-xs ${TEXT_SUBTLE}`}>
                    {night.bedtime} to {night.wakeTime}
                  </span>
                </SurfaceRow>
              )
            })}
          </div>

          <aside className="sleep-night-side space-y-3">
            <SurfacePanel>
              <Eyebrow variant="mini" tone="subtle">Best Night</Eyebrow>
              <StrongText className="mt-2" size="base">{bestNight.date}</StrongText>
              <p className={`mt-1 text-sm/6 ${TEXT_SUBTLE}`}>Score {bestNight.score} · {formatHours(bestNight.duration)}</p>
            </SurfacePanel>

            <SurfacePanel>
              <Eyebrow variant="mini" tone="subtle">Low Recovery</Eyebrow>
              <StrongText className="mt-2" size="base">{lowRecoveryNights} nights</StrongText>
              <p className={`mt-1 text-sm/6 ${TEXT_SUBTLE}`}>Score below 70 over last 7 nights.</p>
            </SurfacePanel>
          </aside>
        </div>
      </SurfaceCard>

      <SurfaceCard as="article">
        <div className="sleep-breakdown-layout">
          <section>
            <Subheading variant="dashboard">Sleep stage averages</Subheading>
            <div className="sleep-stage-grid mt-4">
              {stageAverages.map((stage) => (
                <div key={stage.key}>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block size-2.5 rounded-full ${stage.color}`} />
                      <span className={`font-medium ${TEXT_PRIMARY}`}>{stage.label}</span>
                    </div>
                    <span className={TEXT_SUBTLE}>
                      {formatHours(stage.hours)} · {stage.pct}%
                    </span>
                  </div>
                  <div className={`mt-1.5 h-2 w-full rounded-full ${BAR_TRACK}`}>
                    <div
                      className={`${stage.color} h-2 rounded-full transition-all`}
                      style={{ width: `${stage.pct}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <Subheading variant="dashboard">Sleep score trend</Subheading>
            <div className="mt-6 flex h-52 items-end gap-3">
              {[...SLEEP_HISTORY].reverse().map((night) => {
                const height = Math.max(12, (night.score / maxScore) * 100)
                return (
                  <div key={night.id} className="flex flex-1 flex-col items-center gap-2">
                    <span className={`text-xs font-medium ${TEXT_SECONDARY}`}>{night.score}</span>
                    <div
                      className={`w-full rounded-t-xl ${scoreBarColor(night.score)}`}
                      style={{ height: `${height}%` }}
                    />
                    <span className={`text-xs ${TEXT_SUBTLE}`}>{night.date}</span>
                  </div>
                )
              })}
            </div>
          </section>
        </div>
      </SurfaceCard>
    </div>
  )
}
